import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, Stack, TextField, Typography,
} from '@mui/material';
import CompanyService, { CompanyDetails as Details } from '../../api/services/company.service';
import { useAuthStore } from '../../store/authStore';

// The company's own details: its name, where new drivers report and when.
//
// The manager introduction quotes the address and the time, so they are
// typed once here rather than into every message. Open to AO, CA and BC —
// the people who actually send that message — and hidden from everyone
// else, who could only read it.

type Field = 'name' | 'address' | 'reportTime';

const CompanyDetails: React.FC = () => {
  const { canArea } = useAuthStore();
  // The access matrix's `companySettings`: a BC sees these by default, and
  // only the owner and company admins change them.
  const canSee = canArea('companySettings', 'view');
  const canEdit = canArea('companySettings', 'edit');

  const [saved, setSaved] = useState<Details | null>(null);
  const [draft, setDraft] = useState<Record<Field, string>>({ name: '', address: '', reportTime: '' });
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!canSee) return;
    CompanyService.mine()
      .then((company) => {
        setSaved(company);
        setDraft({ name: company.name, address: company.address, reportTime: company.reportTime });
      })
      .catch(() => setNote({ tone: 'error', text: 'The company details could not be loaded.' }));
  }, [canSee]);

  if (!canSee) return null;

  const save = async (field: Field) => {
    if (!saved || draft[field] === saved[field]) return;
    try {
      const company = await CompanyService.saveMine({ [field]: draft[field] });
      setSaved(company);
      setDraft({ name: company.name, address: company.address, reportTime: company.reportTime });
      setNote({ tone: 'ok', text: 'Saved.' });
    } catch (err: any) {
      setDraft((d) => ({ ...d, [field]: saved[field] }));
      setNote({ tone: 'error', text: err?.response?.data?.message || err?.message || 'That could not be saved.' });
    }
  };

  const bind = (field: Field) => ({
    value: draft[field],
    disabled: !saved || !canEdit,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setDraft((d) => ({ ...d, [field]: e.target.value }));
      setNote(null);
    },
    onBlur: () => save(field),
  });

  return (
    <Card id="company" sx={{ mt: 3, scrollMarginTop: 24 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 0.5 }}>Company</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Where and when new drivers report on their first day. The manager
          introduction message quotes both.
          {!canEdit && ' Only the owner or a company admin can change them.'}
        </Typography>
        <Stack gap={2}>
          <TextField size="small" label="Company name" fullWidth {...bind('name')} />
          <TextField
            size="small" label="Address" fullWidth placeholder="8988 Belvoir View Pl, Lorton"
            helperText="Where new drivers report"
            {...bind('address')}
          />
          <TextField
            size="small" label="Report time" type="time" sx={{ width: 200 }}
            InputLabelProps={{ shrink: true }}
            helperText="First-day start time"
            {...bind('reportTime')}
          />
        </Stack>
        <Typography
          variant="caption"
          color={note?.tone === 'error' ? 'error' : 'text.disabled'}
          sx={{ display: 'block', mt: 1 }}
        >
          {note?.text || ' '}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default CompanyDetails;
